import {FormEvent, useEffect, useMemo, useRef, useState} from 'react'
import {useSearchParams} from 'react-router-dom'
import Select from 'react-select'
import {
  Activity,
  ArrowDown,
  ArrowRight,
  ArrowUp,
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Code2,
  Download,
  Filter,
  Layers3,
  List,
  ListFilter,
  RefreshCw,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  Users,
  X,
} from 'lucide-react'
import {PageTitle} from '../../../_metronic/layout/core'
import {isSuperAdmin} from '../../modules/auth/core/Auth'
import * as API from '../../api'
import {getLogs, LogBucket, LogFilters, LogQuery, LogsResponse} from './logsApi'
import {dateToInput, formatLogDate, initials, LogEntry, logTimestamp} from './logUtils'
import LogDetailsDrawer from './LogDetailsDrawer'
import './LogsScreen.scss'

type Option = {value: string; label: string}

// The user directory is company-scoped, while the audit includes authors from all companies.
// Retain known names across requests so filtering never discards the selected author's label.
const mergeUserOptions = (current: Option[], incoming: Option[]): Option[] => {
  const options = new Map(current.map((option) => [option.value, option]))
  incoming.forEach((option) => {
    if (option.value && option.label?.trim()) options.set(option.value, option)
  })
  return Array.from(options.values())
}
type View = 'table' | 'timeline' | 'groups'
type Group = 'type' | 'user' | 'time'
const number = (value: number) => new Intl.NumberFormat('pt-BR').format(value)
const intervalMs = {minute: 60000, hour: 3600000, day: 86400000}
const intervalLabel = {minute: 'minuto', hour: 'hora', day: 'dia'}
const presets = [
  {id: 'hour', label: 'Última hora'},
  {id: 'today', label: 'Hoje'},
  {id: 'yesterday', label: 'Ontem'},
  {id: '7days', label: '7 dias'},
  {id: '30days', label: '30 dias'},
]
const period = (preset: string) => {
  const end = new Date()
  end.setSeconds(59, 999)
  const start = new Date(end)
  if (preset === 'hour') start.setHours(start.getHours() - 1)
  else {
    start.setHours(0, 0, 0, 0)
    if (preset === 'yesterday') {
      start.setDate(start.getDate() - 1)
      end.setDate(end.getDate() - 1)
      end.setHours(23, 59, 59, 999)
    } else if (preset !== 'today') start.setDate(start.getDate() - (preset === '30days' ? 29 : 6))
  }
  return {start: dateToInput(start), end: dateToInput(end)}
}
const defaults = (): LogFilters => ({
  search: '',
  users: [],
  userIds: [],
  types: [],
  hasData: 'all',
  id: '',
  ...period('7days'),
})
const filtersFromUrl = (params: URLSearchParams): LogFilters => {
  const base = defaults()
  const csv = (name: string) => params.get(name)?.split(',').filter(Boolean) || []
  const validDate = (name: string) => {
    const value = params.get(name)
    return value && !Number.isNaN(new Date(value).getTime())
      ? dateToInput(new Date(value))
      : base[name as 'start' | 'end']
  }
  return {
    search: params.get('search') || '',
    users: csv('users'),
    userIds: csv('userIds'),
    types: csv('types'),
    start: validDate('start'),
    end: validDate('end'),
    hasData:
      params.get('hasData') === 'yes' ? 'yes' : params.get('hasData') === 'no' ? 'no' : 'all',
    id: /^\d+$/.test(params.get('id') || '') ? params.get('id')! : '',
  }
}
const filtersToUrl = (filters: LogFilters) => {
  const params = new URLSearchParams()
  Object.entries(filters).forEach(([key, value]) => {
    const text = Array.isArray(value)
      ? value.join(',')
      : (key === 'start' || key === 'end') && value
      ? new Date(value).toISOString()
      : value
    if (text && text !== 'all') params.set(key, text)
  })
  return params
}
const validFilters = (filters: LogFilters) => {
  if (
    !filters.start ||
    !filters.end ||
    Number.isNaN(new Date(filters.start).getTime()) ||
    Number.isNaN(new Date(filters.end).getTime())
  )
    return 'Informe o início e o fim do período.'
  if (new Date(filters.start) > new Date(filters.end))
    return 'O início do período deve ser anterior ao fim.'
  if (filters.id && !/^[1-9]\d*$/.test(filters.id)) return 'Informe um ID de evento válido.'
  return ''
}
const time = (value: string) => {
  const stamp = logTimestamp(value)
  return Number.isFinite(stamp)
    ? new Date(stamp).toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    : '—'
}
const bucketLabel = (value: string, interval: string) => {
  const stamp = logTimestamp(value)
  if (!Number.isFinite(stamp)) return 'Data indisponível'
  return new Date(stamp).toLocaleString(
    'pt-BR',
    interval === 'day'
      ? {day: '2-digit', month: 'short'}
      : {day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'}
  )
}
const csvCell = (value: unknown) => {
  let text =
    typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value ?? '')
  if (/^[\s]*[=+@-]/.test(text)) text = "'" + text
  return '"' + text.replace(/"/g, '""') + '"'
}
const exportPage = (rows: LogEntry[]) => {
  const header = ['ID', 'Data e hora', 'Usuário', 'Tipo', 'Evento', 'Dados']
  const csv = [
    header,
    ...rows.map((row) => [
      row.id_log,
      formatLogDate(row.created_at),
      row.user || 'Sistema / usuário removido',
      row.type,
      row.name,
      row.data,
    ]),
  ]
    .map((row) => row.map(csvCell).join(';'))
    .join('\r\n')
  const url = URL.createObjectURL(new Blob(['\uFEFF' + csv], {type: 'text/csv;charset=utf-8;'}))
  const link = document.createElement('a')
  link.href = url
  link.download = 'logs-pagina-' + new Date().toISOString().slice(0, 10) + '.csv'
  link.click()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

const LogsWorkspace = ({className = ''}: {className?: string}) => {
  const [params, setParams] = useSearchParams()
  const [filters, setFilters] = useState<LogFilters>(() => filtersFromUrl(params))
  const [draft, setDraft] = useState(filters)
  const [users, setUsers] = useState<Option[]>([])
  const [types, setTypes] = useState<Option[]>([])
  const [result, setResult] = useState<LogsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [optionsError, setOptionsError] = useState(false)
  const [validation, setValidation] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [sort, setSort] = useState<LogQuery['sort']>('desc')
  const [bucket, setBucket] = useState<LogQuery['bucket']>('auto')
  const [view, setView] = useState<View>('table')
  const [group, setGroup] = useState<Group>('type')
  const [groupSearch, setGroupSearch] = useState('')
  const [advanced, setAdvanced] = useState(false)
  const [selected, setSelected] = useState<LogEntry | null>(null)
  const [refresh, setRefresh] = useState(0)
  const [updated, setUpdated] = useState<Date | null>(null)
  const [preset, setPreset] = useState(params.has('start') ? '' : '7days')
  const [copied, setCopied] = useState(false)
  const [history, setHistory] = useState<
    Array<{
      filters: LogFilters
      view: View
      bucket: LogQuery['bucket']
      sort: LogQuery['sort']
      preset: string
    }>
  >([])
  const requestId = useRef(0)
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const dirty = JSON.stringify(draft) !== JSON.stringify(filters)

  useEffect(() => {
    let active = true
    Promise.allSettled([API.listUsers(), API.listLogTypes()]).then(([userResult, typeResult]) => {
      if (!active) return
      if (userResult.status === 'fulfilled' && Array.isArray(userResult.value?.data?.users)) {
        setUsers((current) =>
          mergeUserOptions(
            current,
            userResult.value.data.users.map((user: any) => ({
              value: user.guid ? String(user.guid) : '',
              label: user.name,
            }))
          )
        )
      } else setOptionsError(true)
      if (typeResult.status === 'fulfilled' && Array.isArray(typeResult.value?.data)) {
        setTypes(
          typeResult.value.data.map((type: any) => ({
            value: String(type.id_log_type),
            label: type.name,
          }))
        )
      } else setOptionsError(true)
    })
    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    const id = ++requestId.current
    const invalid = validFilters(filters)
    if (invalid) {
      setError(invalid)
      setLoading(false)
      return
    }
    setLoading(true)
    setError('')
    getLogs({...filters, page, pageSize, sort, bucket})
      .then((response) => {
        if (id !== requestId.current) return
        if (page > Math.max(1, response.pagination.totalPages)) {
          setPage(Math.max(1, response.pagination.totalPages))
          return
        }
        setResult(response)
        setUsers((current) =>
          mergeUserOptions(current, [
            ...response.distribution.users.map((user) => ({
              value: user.guid || '',
              label: user.name,
            })),
            ...response.data.map((log) => ({
              value: log.user_guid || '',
              label: log.user || '',
            })),
          ])
        )
        setUpdated(new Date())
      })
      .catch((reason) => {
        if (id === requestId.current) {
          setError(reason?.message || 'Não foi possível carregar os logs. Tente novamente.')
          setResult(null)
        }
      })
      .finally(() => {
        if (id === requestId.current) setLoading(false)
      })
    return () => {
      requestId.current++
    }
  }, [filters, page, pageSize, sort, bucket, refresh])

  const apply = (next: LogFilters) => {
    const invalid = validFilters(next)
    setValidation(invalid)
    if (invalid) return
    setFilters(next)
    setDraft(next)
    setPage(1)
    setParams(filtersToUrl(next), {replace: true})
  }
  const remember = () =>
    setHistory((previous) => [...previous.slice(-9), {filters, view, bucket, sort, preset}])
  const goBack = () => {
    const previous = history[history.length - 1]
    if (!previous) return
    apply(previous.filters)
    setView(previous.view)
    setBucket(previous.bucket)
    setSort(previous.sort)
    setPreset(previous.preset)
    setHistory((items) => items.slice(0, -1))
  }
  const refreshLogs = () => {
    if (preset) {
      const next = {...filters, ...period(preset)}
      setFilters(next)
      if (!dirty) setDraft(next)
      setParams(filtersToUrl(next), {replace: true})
      setPage(1)
    }
    setRefresh((value) => value + 1)
  }
  const reset = () => {
    setHistory([])
    setPreset('7days')
    setBucket('auto')
    apply(defaults())
  }
  const submit = (event: FormEvent) => {
    event.preventDefault()
    setHistory([])
    apply(draft)
  }
  const selectPreset = (id: string) => {
    setHistory([])
    setPreset(id)
    apply({...draft, ...period(id)})
  }
  const patchDraft = (patch: Partial<LogFilters>) => {
    setDraft((current) => ({...current, ...patch}))
    setValidation('')
  }
  const filterType = (id: number | null) => {
    if (id != null) {
      remember()
      apply({...filters, types: [String(id)]})
      setView('table')
    }
  }
  const filterUser = (id: number | null, guid?: string | null, name?: string | null) => {
    if (guid && name) {
      setUsers((current) => mergeUserOptions(current, [{value: guid, label: name}]))
    }
    remember()
    apply({
      ...filters,
      id: '',
      users: guid ? [guid] : [],
      userIds: guid ? [] : [id ? String(id) : 'system'],
    })
    setSelected(null)
    setView('timeline')
  }
  const focusBucket = (item: LogBucket) => {
    if (!result) return
    remember()
    const begin = logTimestamp(item.start)
    const finish = begin + intervalMs[result.timeline.interval] * (result.timeline.step || 1) - 1000
    const start = new Date(Math.max(begin, new Date(filters.start).getTime()))
    const end = new Date(Math.min(finish, new Date(filters.end).getTime()))
    setPreset('')
    apply({...filters, start: dateToInput(start), end: dateToInput(end)})
    setBucket('auto')
    setView('timeline')
  }
  const investigate = (log: LogEntry) => {
    const stamp = logTimestamp(log.created_at)
    if (!Number.isFinite(stamp)) return
    remember()
    setPreset('')
    apply({
      ...defaults(),
      start: dateToInput(new Date(stamp - 15 * 60000)),
      end: dateToInput(new Date(stamp + 15 * 60000)),
    })
    setSort('asc')
    setBucket('minute')
    setSelected(null)
    setView('timeline')
  }
  const copyLink = async () => {
    try {
      const url = new URL(window.location.href)
      url.search = filtersToUrl(filters).toString()
      await navigator.clipboard.writeText(url.toString())
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setValidation('Não foi possível copiar. Você pode copiar o endereço da página no navegador.')
    }
  }
  const activeChips = [
    ...(filters.search
      ? [
          {
            key: 'search',
            text: 'Busca: ' + filters.search,
            remove: () => apply({...filters, search: ''}),
          },
        ]
      : []),
    ...filters.users.map((id) => ({
      key: 'user-' + id,
      text: users.find((user) => user.value === id)?.label || 'Usuário ' + id,
      remove: () => apply({...filters, users: filters.users.filter((value) => value !== id)}),
    })),
    ...filters.userIds.map((id) => ({
      key: 'uid-' + id,
      text: id === 'system' ? 'Sistema' : 'Usuário #' + id,
      remove: () => apply({...filters, userIds: filters.userIds.filter((value) => value !== id)}),
    })),
    ...filters.types.map((id) => ({
      key: 'type-' + id,
      text: types.find((type) => type.value === id)?.label || 'Tipo #' + id,
      remove: () => apply({...filters, types: filters.types.filter((value) => value !== id)}),
    })),
    ...(filters.hasData !== 'all'
      ? [
          {
            key: 'data',
            text: filters.hasData === 'yes' ? 'Com dados adicionais' : 'Sem dados adicionais',
            remove: () => apply({...filters, hasData: 'all'}),
          },
        ]
      : []),
    ...(filters.id
      ? [{key: 'id', text: 'Evento #' + filters.id, remove: () => apply({...filters, id: ''})}]
      : []),
  ]
  const summary = result?.summary
  const buckets = result?.timeline.buckets || []
  const maxBucket = Math.max(1, ...buckets.map((item) => item.count))
  const topTypes = result?.distribution.types.slice(0, 5) || []
  const bucketStep = result?.timeline.step || 1
  const bucketDescription =
    bucketStep +
    ' ' +
    intervalLabel[result?.timeline.interval || 'day'] +
    (bucketStep > 1 ? 's' : '')
  const timelineRows = useMemo(() => {
    const groups: Array<{day: string; rows: LogEntry[]}> = []
    result?.data.forEach((log) => {
      const stamp = logTimestamp(log.created_at)
      const day = Number.isFinite(stamp)
        ? new Date(stamp).toLocaleDateString('pt-BR', {
            weekday: 'long',
            day: '2-digit',
            month: 'long',
            year: 'numeric',
          })
        : 'Data indisponível'
      const last = groups[groups.length - 1]
      if (last?.day === day) last.rows.push(log)
      else groups.push({day, rows: [log]})
    })
    return groups
  }, [result])
  const groups =
    (group === 'type'
      ? result?.distribution.types.map((item) => ({
          key: 'type-' + item.id,
          name: item.name || 'Tipo indisponível',
          count: item.count,
          hint: 'Tipo de evento',
          action: () => filterType(item.id),
          disabled: item.id == null,
        }))
      : group === 'user'
      ? result?.distribution.users.map((item) => ({
          key: 'user-' + item.id,
          name: item.name || 'Sistema / usuário removido',
          count: item.count,
          hint: item.id ? 'Usuário #' + item.id : 'Sistema',
          action: () => filterUser(item.id, item.guid, item.name),
          disabled: false,
        }))
      : buckets
          .filter((item) => item.count > 0)
          .map((item) => ({
            key: item.start,
            name: bucketLabel(item.start, result?.timeline.interval || 'day'),
            count: item.count,
            hint: 'Intervalo de ' + bucketDescription,
            action: () => focusBucket(item),
            disabled: false,
          }))
    )?.filter((item) =>
      item.name.toLocaleLowerCase('pt-BR').includes(groupSearch.toLocaleLowerCase('pt-BR'))
    ) || []

  return (
    <div className={'logs-page ' + className}>
      <PageTitle>Sistema de Logs</PageTitle>
      <header className='logs-hero'>
        <div className='logs-hero-content'>
          <div className='logs-eyebrow'>
            <span /> RASTREABILIDADE & AUDITORIA
          </div>
          <h1>Cada ação. Todo o contexto.</h1>
          <p>Explore os registros do sistema e reconstrua o que aconteceu, quando e com quem.</p>
          <div className='logs-hero-meta'>
            <ShieldCheck size={14} /> Acesso Super Admin <span>•</span> Horários em {zone}
          </div>
        </div>
        <div className='logs-hero-mark' aria-hidden='true'>
          <Activity size={56} strokeWidth={1.2} />
          <span>SISTEMA DE LOGS</span>
          <div>
            <i />
            <i />
            <i />
            <i />
            <i />
            <i />
            <i />
            <i />
            <i />
            <i />
            <i />
          </div>
        </div>
      </header>

      <form className='logs-panel logs-filters' onSubmit={submit}>
        <div className='logs-panel-heading'>
          <div>
            <h2>
              <SlidersHorizontal size={17} /> Explore os registros
            </h2>
            <p>Combine filtros para encontrar o momento certo.</p>
          </div>
          <button type='button' className='logs-text-button' onClick={reset}>
            Limpar filtros
          </button>
        </div>
        <div className='logs-search-row'>
          <div className='logs-search'>
            <Search size={18} />
            <input
              aria-label='Buscar nos logs'
              placeholder='Busque por ação, usuário, tipo ou conteúdo dos dados…'
              value={draft.search}
              onChange={(event) => patchDraft({search: event.target.value})}
            />
            <kbd>Enter</kbd>
          </div>
          <button
            type='button'
            className={'logs-button ' + (advanced ? 'is-active' : '')}
            aria-expanded={advanced}
            aria-controls='logs-advanced'
            onClick={() => setAdvanced(!advanced)}
          >
            <Filter size={15} /> Mais filtros
            {draft.hasData !== 'all' || draft.id ? <span className='logs-dot' /> : null}
          </button>
          <button className='logs-button logs-button-primary' type='submit'>
            <Search size={15} /> Aplicar filtros
          </button>
        </div>
        <div className='logs-filter-grid'>
          <div className='logs-field'>
            <label htmlFor='logs-users'>Usuários</label>
            <Select<Option, true>
              inputId='logs-users'
              isMulti
              isClearable
              classNamePrefix='logs-select'
              placeholder='Todos os usuários'
              options={users}
              value={draft.users.map(
                (id) => users.find((user) => user.value === id) || {value: id, label: id}
              )}
              onChange={(value) =>
                patchDraft({users: value.map((item) => item.value), userIds: []})
              }
              noOptionsMessage={() => 'Nenhum usuário encontrado'}
            />
          </div>
          <div className='logs-field'>
            <label htmlFor='logs-types'>Tipos de evento</label>
            <Select<Option, true>
              inputId='logs-types'
              isMulti
              isClearable
              classNamePrefix='logs-select'
              placeholder='Todos os tipos'
              options={types}
              value={draft.types.map(
                (id) => types.find((type) => type.value === id) || {value: id, label: 'Tipo #' + id}
              )}
              onChange={(value) => patchDraft({types: value.map((item) => item.value)})}
              noOptionsMessage={() => 'Nenhum tipo encontrado'}
            />
          </div>
          <div className='logs-field'>
            <label htmlFor='logs-start'>De · data e hora</label>
            <input
              id='logs-start'
              type='datetime-local'
              step='1'
              value={draft.start}
              onChange={(event) => {
                setPreset('')
                patchDraft({start: event.target.value})
              }}
            />
          </div>
          <div className='logs-field'>
            <label htmlFor='logs-end'>Até · data e hora</label>
            <input
              id='logs-end'
              type='datetime-local'
              step='1'
              value={draft.end}
              onChange={(event) => {
                setPreset('')
                patchDraft({end: event.target.value})
              }}
            />
          </div>
        </div>
        {advanced && (
          <div id='logs-advanced' className='logs-advanced'>
            <div className='logs-field'>
              <label htmlFor='logs-payload'>Dados adicionais</label>
              <select
                id='logs-payload'
                value={draft.hasData}
                onChange={(event) =>
                  patchDraft({hasData: event.target.value as LogFilters['hasData']})
                }
              >
                <option value='all'>Todos os registros</option>
                <option value='yes'>Com dados adicionais</option>
                <option value='no'>Sem dados adicionais</option>
              </select>
            </div>
            <div className='logs-field'>
              <label htmlFor='logs-id'>ID exato do evento</label>
              <input
                id='logs-id'
                inputMode='numeric'
                placeholder='Ex.: 10248'
                value={draft.id}
                onChange={(event) => patchDraft({id: event.target.value})}
              />
            </div>
            <div className='logs-filter-help'>
              <Code2 size={19} />
              <span>
                A busca também encontra termos dentro dos dados registrados em cada evento.
              </span>
            </div>
          </div>
        )}
        <div className='logs-filter-bottom'>
          <div className='logs-presets'>
            <CalendarDays size={14} />
            {presets.map((item) => (
              <button
                type='button'
                key={item.id}
                className={preset === item.id ? 'active' : ''}
                onClick={() => selectPreset(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>
          <span className={'logs-draft-state ' + (dirty ? 'is-dirty' : '')}>
            {dirty ? 'Há filtros ainda não aplicados' : 'Filtros aplicados à consulta'}
          </span>
        </div>
        {validation && (
          <div className='logs-inline-error' role='alert'>
            {validation}
          </div>
        )}
        {optionsError && (
          <div className='logs-inline-error' role='status'>
            Não foi possível carregar todas as opções de usuários e tipos. A busca e os demais
            filtros continuam disponíveis.
          </div>
        )}
        {history.length > 0 && (
          <button type='button' className='logs-back-button' onClick={goBack}>
            <ChevronLeft size={14} />
            Voltar ao recorte anterior
          </button>
        )}
        {activeChips.length > 0 && (
          <div className='logs-chips'>
            <span>Filtros ativos</span>
            {activeChips.map((chip) => (
              <button
                type='button'
                key={chip.key}
                onClick={chip.remove}
                title={'Remover filtro: ' + chip.text}
              >
                {chip.text}
                <X size={12} />
              </button>
            ))}
          </div>
        )}
      </form>

      <div className='logs-metrics' aria-busy={loading}>
        {[
          {
            title: 'Eventos encontrados',
            value: summary?.total,
            icon: Activity,
            color: 'blue',
            hint: 'Em todo o período filtrado',
          },
          {
            title: 'Autores envolvidos',
            value: summary?.users,
            icon: Users,
            color: 'teal',
            hint: 'Usuários e ações do sistema',
          },
          {
            title: 'Tipos de evento',
            value: summary?.types,
            icon: Layers3,
            color: 'violet',
            hint: 'Categorias de atividade',
          },
          {
            title: 'Com dados adicionais',
            value: summary?.withData,
            icon: Code2,
            color: 'amber',
            hint: 'Registros com contexto para explorar',
          },
        ].map((metric) => (
          <div className={'logs-metric logs-metric--' + metric.color} key={metric.title}>
            <div>
              <span>{metric.title}</span>
              <metric.icon size={18} />
            </div>
            <strong>{loading ? '—' : metric.value == null ? '—' : number(metric.value)}</strong>
            <small>{metric.hint}</small>
          </div>
        ))}
      </div>

      <div className='logs-overview' aria-busy={loading}>
        <section className='logs-panel logs-activity'>
          <div className='logs-panel-heading'>
            <div>
              <h2>
                <Activity size={17} /> Atividade no período
              </h2>
              <p>Clique em um intervalo para investigar os eventos.</p>
            </div>
            <select
              aria-label='Intervalo do gráfico'
              className='logs-small-select'
              value={bucket}
              onChange={(event) => setBucket(event.target.value as LogQuery['bucket'])}
            >
              <option value='auto'>Intervalo automático</option>
              <option value='minute'>Por minuto</option>
              <option value='hour'>Por hora</option>
              <option value='day'>Por dia</option>
            </select>
          </div>
          {loading ? (
            <div className='logs-chart-placeholder'>
              <span className='logs-spinner' />
              Consultando atividade…
            </div>
          ) : !buckets.length || !summary?.total ? (
            <div className='logs-chart-placeholder'>Nenhuma atividade neste período</div>
          ) : (
            <>
              <div className='logs-chart-summary'>
                <strong>
                  {number(summary.total)} <span>eventos</span>
                </strong>
                <span>Intervalos de {bucketDescription}</span>
              </div>
              <div
                className={'logs-histogram' + (buckets.length > 90 ? ' logs-histogram--dense' : '')}
                role='group'
                aria-label='Volume de eventos por intervalo'
              >
                {buckets.map((item) => (
                  <button
                    type='button'
                    className='logs-histogram-column'
                    key={item.start}
                    disabled={!item.count}
                    title={
                      bucketLabel(item.start, result!.timeline.interval) +
                      ' · ' +
                      number(item.count) +
                      ' eventos'
                    }
                    aria-label={
                      bucketLabel(item.start, result!.timeline.interval) +
                      ': ' +
                      number(item.count) +
                      ' eventos. Investigar intervalo'
                    }
                    onClick={() => focusBucket(item)}
                  >
                    <span
                      style={{
                        height: Math.max(item.count ? 5 : 2, (item.count / maxBucket) * 100) + '%',
                      }}
                    />
                  </button>
                ))}
              </div>
              <div className='logs-chart-axis'>
                <span>{bucketLabel(buckets[0].start, result!.timeline.interval)}</span>
                <span>
                  {bucketLabel(buckets[buckets.length - 1].start, result!.timeline.interval)}
                </span>
              </div>
            </>
          )}
        </section>
        <section className='logs-panel logs-top-types'>
          <div className='logs-panel-heading'>
            <div>
              <h2>
                <Layers3 size={17} /> Eventos por tipo
              </h2>
              <p>Principais atividades da consulta</p>
            </div>
          </div>
          {loading ? (
            <div className='logs-chart-placeholder'>Carregando distribuição…</div>
          ) : topTypes.length ? (
            <div className='logs-rankings'>
              {topTypes.map((item, index) => (
                <button
                  key={String(item.id)}
                  type='button'
                  disabled={item.id == null}
                  onClick={() => filterType(item.id)}
                >
                  <div>
                    <span>
                      <i>{String(index + 1).padStart(2, '0')}</i>
                      {item.name}
                    </span>
                    <strong>{number(item.count)}</strong>
                  </div>
                  <div className='logs-ranking-track'>
                    <span
                      style={{width: (item.count / Math.max(1, summary?.total || 0)) * 100 + '%'}}
                    />
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className='logs-chart-placeholder'>Nenhum tipo encontrado</div>
          )}
        </section>
      </div>

      <section className='logs-panel logs-results' aria-busy={loading}>
        <div className='logs-panel-heading logs-results-heading'>
          <div>
            <h2>
              Explore os acontecimentos{' '}
              <span className='logs-count'>{loading ? '…' : number(summary?.total || 0)}</span>
            </h2>
            <p>
              {updated
                ? 'Atualizado às ' + updated.toLocaleTimeString('pt-BR')
                : 'Histórico de ações do sistema'}{' '}
              · {formatLogDate(filters.start)} a {formatLogDate(filters.end)}
            </p>
          </div>
          <div className='logs-result-actions'>
            <button
              type='button'
              className='logs-icon-button'
              onClick={refreshLogs}
              disabled={loading}
              aria-label='Atualizar logs'
              title='Atualizar logs'
            >
              <RefreshCw size={16} />
            </button>
            <button type='button' className='logs-button' onClick={copyLink}>
              {copied ? <Check size={14} /> : <ArrowRight size={14} />}
              {copied ? 'Link copiado' : 'Copiar consulta'}
            </button>
            <button
              type='button'
              className='logs-button'
              onClick={() => exportPage(result?.data || [])}
              disabled={loading || !result?.data.length}
            >
              <Download size={14} /> Exportar página
            </button>
          </div>
        </div>
        <div className='logs-view-toolbar'>
          <div className='logs-view-tabs' role='group' aria-label='Visualização dos registros'>
            {[
              {id: 'table', label: 'Lista de eventos', icon: List},
              {id: 'timeline', label: 'Linha do tempo', icon: Clock3},
              {id: 'groups', label: 'Agrupamentos', icon: Layers3},
            ].map((tab) => (
              <button
                key={tab.id}
                type='button'
                aria-pressed={view === tab.id}
                className={view === tab.id ? 'active' : ''}
                onClick={() => setView(tab.id as View)}
              >
                <tab.icon size={15} />
                {tab.label}
              </button>
            ))}
          </div>
          <button
            type='button'
            className='logs-sort'
            hidden={view === 'groups'}
            onClick={() => {
              setSort(sort === 'desc' ? 'asc' : 'desc')
              setPage(1)
            }}
          >
            {sort === 'desc' ? <ArrowDown size={14} /> : <ArrowUp size={14} />}
            {sort === 'desc' ? 'Mais recentes primeiro' : 'Mais antigos primeiro'}
          </button>
        </div>

        {loading ? (
          <div className='logs-empty' role='status'>
            <span className='logs-spinner' />
            <h3>Buscando os acontecimentos</h3>
            <p>Consultando registros e agrupamentos do período.</p>
          </div>
        ) : error ? (
          <div className='logs-empty logs-empty-error' role='alert'>
            <Activity size={30} />
            <h3>Não foi possível carregar os logs</h3>
            <p>{error}</p>
            <button
              type='button'
              className='logs-button'
              onClick={() => setRefresh((value) => value + 1)}
            >
              <RefreshCw size={14} /> Tentar novamente
            </button>
          </div>
        ) : !result?.data.length ? (
          <div className='logs-empty'>
            <Search size={30} />
            <h3>Nenhum evento encontrado</h3>
            <p>Experimente ampliar o período ou remover algum filtro.</p>
            <button type='button' className='logs-button' onClick={reset}>
              Limpar filtros
            </button>
          </div>
        ) : view === 'table' ? (
          <div className='logs-table-wrap'>
            <table className='logs-table'>
              <thead>
                <tr>
                  <th>Data e hora</th>
                  <th>Evento / descrição</th>
                  <th>Usuário</th>
                  <th>Tipo de evento</th>
                  <th>
                    <span className='visually-hidden'>Detalhes</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {result.data.map((log) => (
                  <tr key={log.id_log}>
                    <td>
                      <span className='logs-time'>{time(log.created_at)}</span>
                      <small>
                        {Number.isFinite(logTimestamp(log.created_at))
                          ? new Date(logTimestamp(log.created_at)).toLocaleDateString('pt-BR')
                          : '—'}
                      </small>
                    </td>
                    <td>
                      <button
                        className='logs-event-link'
                        type='button'
                        onClick={() => setSelected(log)}
                      >
                        {log.name || 'Evento sem descrição'}
                      </button>
                      <small className='logs-mono'>#{log.id_log}</small>
                    </td>
                    <td>
                      <div className='logs-user'>
                        <span className='logs-avatar'>{initials(log.user || 'Sistema')}</span>
                        <span>{log.user || 'Sistema / usuário removido'}</span>
                      </div>
                    </td>
                    <td>
                      <span className='logs-type-badge'>{log.type || 'Tipo indisponível'}</span>
                    </td>
                    <td>
                      <button
                        className='logs-open'
                        type='button'
                        aria-label={'Ver detalhes do evento ' + log.id_log}
                        onClick={() => setSelected(log)}
                      >
                        <ArrowRight size={17} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : view === 'timeline' ? (
          <div className='logs-timeline'>
            <div className='logs-scope-note'>
              <Clock3 size={14} /> Sequência dos eventos desta página. Abra um evento para
              investigar os 15 minutos antes e depois.
            </div>
            {timelineRows.map((day) => (
              <div className='logs-timeline-day' key={day.day}>
                <h3>
                  {day.day}
                  <span>{number(day.rows.length)} eventos nesta página</span>
                </h3>
                {day.rows.map((log) => (
                  <div className='logs-timeline-row' key={log.id_log}>
                    <time>{time(log.created_at)}</time>
                    <span className='logs-timeline-dot' />
                    <button
                      type='button'
                      className='logs-timeline-card'
                      onClick={() => setSelected(log)}
                    >
                      <div>
                        <span className='logs-type-badge'>{log.type || 'Tipo indisponível'}</span>
                        <span className='logs-mono'>#{log.id_log}</span>
                      </div>
                      <strong>{log.name || 'Evento sem descrição'}</strong>
                      <div className='logs-timeline-bottom'>
                        <span>
                          <Users size={13} />
                          {log.user || 'Sistema / usuário removido'}
                        </span>
                        <span>
                          Explorar evento <ArrowRight size={14} />
                        </span>
                      </div>
                    </button>
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) : (
          <div className='logs-groups'>
            <div className='logs-groups-toolbar'>
              <div className='logs-field'>
                <label htmlFor='logs-group'>Agrupar por</label>
                <select
                  id='logs-group'
                  value={group}
                  onChange={(event) => {
                    setGroup(event.target.value as Group)
                    setGroupSearch('')
                  }}
                >
                  <option value='type'>Tipo de evento</option>
                  <option value='user'>Usuário</option>
                  <option value='time'>Intervalo de tempo</option>
                </select>
              </div>
              <div className='logs-search'>
                <Search size={16} />
                <input
                  aria-label='Buscar agrupamento'
                  placeholder='Encontrar um grupo…'
                  value={groupSearch}
                  onChange={(event) => setGroupSearch(event.target.value)}
                />
              </div>
              <span>{number(groups.length)} grupos · todos os resultados</span>
            </div>
            <div className='logs-group-grid'>
              {groups.map((item) => (
                <button
                  type='button'
                  className='logs-group-card'
                  disabled={item.disabled}
                  key={item.key}
                  onClick={item.action}
                >
                  <div className='logs-group-icon'>
                    {group === 'user' ? (
                      <Users size={18} />
                    ) : group === 'time' ? (
                      <Clock3 size={18} />
                    ) : (
                      <Layers3 size={18} />
                    )}
                    <ArrowRight size={16} />
                  </div>
                  <small>{item.hint}</small>
                  <h3>{item.name}</h3>
                  <div className='logs-group-value'>
                    <strong>{number(item.count)}</strong>
                    <span>
                      eventos ·{' '}
                      {((item.count / Math.max(1, summary?.total || 0)) * 100).toLocaleString(
                        'pt-BR',
                        {maximumFractionDigits: 1}
                      )}
                      %
                    </span>
                  </div>
                  <div className='logs-ranking-track'>
                    <span
                      style={{width: (item.count / Math.max(1, summary?.total || 0)) * 100 + '%'}}
                    />
                  </div>
                </button>
              ))}
            </div>
            {!groups.length && (
              <div className='logs-empty'>
                <ListFilter size={26} />
                <p>Nenhum grupo corresponde à busca.</p>
              </div>
            )}
          </div>
        )}

        {!loading && !error && !!result?.data.length && view !== 'groups' && (
          <footer className='logs-pagination'>
            <span>
              <strong>
                {number((page - 1) * pageSize + 1)}–
                {number((page - 1) * pageSize + result.data.length)}
              </strong>{' '}
              de {number(result.pagination.total)} eventos
            </span>
            <div>
              <label htmlFor='logs-page-size'>Por página</label>
              <select
                id='logs-page-size'
                value={pageSize}
                onChange={(event) => {
                  setPageSize(Number(event.target.value))
                  setPage(1)
                }}
              >
                {[25, 50, 100].map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
              <button
                className='logs-icon-button'
                type='button'
                aria-label='Página anterior'
                disabled={!result.pagination.hasPrevious}
                onClick={() => setPage(page - 1)}
              >
                <ChevronLeft size={16} />
              </button>
              <span>
                {number(page)} / {number(Math.max(1, result.pagination.totalPages))}
              </span>
              <button
                className='logs-icon-button'
                type='button'
                aria-label='Próxima página'
                disabled={!result.pagination.hasNext}
                onClick={() => setPage(page + 1)}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </footer>
        )}
      </section>
      <div className='logs-footer-note'>
        <ShieldCheck size={13} /> Indicadores e agrupamentos consideram todos os registros que
        atendem aos filtros.
      </div>
      <LogDetailsDrawer
        log={selected}
        onClose={() => setSelected(null)}
        onInvestigate={investigate}
        onFilterUser={(log) => filterUser(log.user_id, log.user_guid, log.user)}
      />
    </div>
  )
}

const LogsScreen = ({className}: {className?: string}) =>
  isSuperAdmin() ? (
    <LogsWorkspace className={className} />
  ) : (
    <div className='logs-page logs-empty'>
      <ShieldCheck size={30} />
      <h3>Acesso restrito</h3>
      <p>O Sistema de Logs está disponível para usuários Super Admin.</p>
    </div>
  )

export default LogsScreen
